import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Typography } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './google-translate-hide.css';


import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { PWAInstallProvider } from './contexts/PWAInstallContext';
import Navbar from './components/Layout/Navbar';
import BottomNavBar from './components/Layout/BottomNavBar';
import InstallPrompt from './components/InstallPrompt/InstallPrompt';
import LocationBackfillPrompt from './components/Location/LocationBackfillPrompt';
import NotificationOptIn from './components/Notifications/NotificationOptIn';
// Home is the landing route — keep it eager so first paint has no lazy waterfall.
import Home from './pages/Home';
import { trackPageView } from './utils/visitorTracking';

// Route-level code splitting: every non-landing page is lazy-loaded so its
// JavaScript is only fetched when the route is actually visited. This keeps the
// initial bundle small and dramatically improves Time-to-Interactive.
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const WorkerSchedulePage = lazy(() => import('./pages/Worker/WorkerSchedulePage'));
const WorkerProfile = lazy(() => import('./pages/Worker/WorkerProfile'));
const WorkerIdentityCard = lazy(() => import('./pages/Worker/WorkerIdentityCard'));
const VerifyWorker = lazy(() => import('./pages/Public/VerifyWorker'));
const SearchWorkers = lazy(() => import('./pages/Owner/SearchWorkers'));
const ViewWorkerProfile = lazy(() => import('./pages/Owner/ViewWorkerProfile'));
const BookingDetails = lazy(() => import('./pages/Bookings/BookingDetails'));
const MyBookings = lazy(() => import('./pages/Bookings/MyBookings'));
const BookWorker = lazy(() => import('./pages/Bookings/BookWorker'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const CompleteBasicProfile = lazy(() => import('./pages/Profile/CompleteBasicProfile'));
const CompleteWorkerProfile = lazy(() => import('./pages/Profile/CompleteWorkerProfile'));
const NotificationsPage = lazy(() => import('./pages/Notifications/Notifications'));
const JobRequestsList = lazy(() => import('./pages/JobRequests/JobRequestsList'));
const MyPostedJobRequests = lazy(() => import('./pages/JobRequests/MyPostedJobRequests'));
const JobRequestDetails = lazy(() => import('./pages/JobRequests/JobRequestDetails'));
const JobRequestEnquiry = lazy(() => import('./pages/JobRequests/JobRequestEnquiry'));
const PostWorkRequest = lazy(() => import('./pages/JobRequests/PostWorkRequest'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/Legal/TermsAndConditions'));
const FeedbackPage = lazy(() => import('./pages/Feedback'));
const AboutUs = lazy(() => import('./pages/About/AboutUs'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    // Keep English looking like the original design (Roboto-first),
    // but allow Indic scripts to fall back to Noto families to avoid glyph clipping.
    fontFamily: [
      'Roboto',
      '"Noto Sans"',
      '"Noto Sans Tamil"',
      '"Noto Sans Malayalam"',
      '"Noto Sans Kannada"',
      '"Noto Sans Meetei Mayek"',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// Records an anonymous page view (device/OS/browser/screen) on every route change
const VisitorTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// Wraps the routed content and reserves bottom space on mobile so pages clear
// the fixed BottomNavBar. The Booking Details screen (/bookings/:id) hides that
// tab bar in favour of its own sticky action bar, so it needs no reserved space.
const RoutedContent = ({ children }) => {
  const location = useLocation();
  const isBookingDetailsPage = /^\/bookings\/[^/]+$/.test(location.pathname);
  return (
    <Box sx={{ pb: isBookingDetailsPage ? 0 : { xs: 'calc(66px + env(safe-area-inset-bottom))', md: 0 } }}>
      {children}
    </Box>
  );
};

// Loading Screen Component
const LoadingScreen = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#DDD0C8',
        flexDirection: 'column',
        gap: 3
      }}
    >
      <CircularProgress size={50} sx={{ color: '#323232' }} />
      <Typography variant="body1" sx={{ color: '#323232' }}>
        Loading...
      </Typography>
    </Box>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { loading, isAuthenticated, hasToken } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  // Allow access if we have a token even if the user object is still hydrating.
  // Otherwise send to login, preserving the intended destination so deep links
  // (e.g. a WhatsApp booking link) return the user to the same page after login.
  if (isAuthenticated || hasToken) {
    return children;
  }
  const redirectTarget = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?redirect=${redirectTarget}`} replace />;
};

// Route guard: requires basic profile (personal details + address) to be complete
const RequireBasicProfile = ({ children }) => {
  const { loading, isAuthenticated, hasToken, isBasicProfileComplete, user } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!(isAuthenticated || hasToken)) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  // While user object is still loading, allow through
  if (!user) return children;
  if (!isBasicProfileComplete) {
    return <Navigate to={`/complete-profile?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return children;
};

// Route guard: requires worker profile (skills + address) to be complete
const RequireWorkerProfile = ({ children }) => {
  const { loading, isAuthenticated, hasToken, isBasicProfileComplete, isWorkerProfileComplete, user } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!(isAuthenticated || hasToken)) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (!user) return children;
  if (!isBasicProfileComplete) {
    return <Navigate to={`/complete-profile?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (!isWorkerProfileComplete) {
    return <Navigate to={`/complete-worker-profile?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { loading, isAuthenticated, hasToken } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (isAuthenticated || hasToken) {
    // Honour ?redirect= so login can chain to complete-profile / book-worker
    const redirectParam = new URLSearchParams(location.search).get('redirect');
    return <Navigate to={redirectParam || '/'} replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SocketProvider>
          <PWAInstallProvider>
          <Router>
            <VisitorTracker />
            <div className="App">
              <Navbar />
              {/* Bottom padding on mobile so page content clears the fixed
                  BottomNavBar. Includes the device safe-area inset so content
                  is never hidden behind on-screen gesture bars (common on
                  Android: OPPO/Vivo/Xiaomi/Samsung). Skipped on Booking Details,
                  which hides the tab bar and uses its own sticky action bar. */}
              <RoutedContent>
              <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/job-requests/:jobRequestId/enquire" element={<JobRequestEnquiry />} />
                <Route path="/login" element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } />
                <Route path="/register" element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                } />
                
                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/complete-profile" element={
                  <ProtectedRoute>
                    <CompleteBasicProfile />
                  </ProtectedRoute>
                } />
                <Route path="/complete-worker-profile" element={
                  <ProtectedRoute>
                    <CompleteWorkerProfile />
                  </ProtectedRoute>
                } />
                <Route path="/worker-schedule" element={
                  <ProtectedRoute>
                    <WorkerSchedulePage />
                  </ProtectedRoute>
                } />
                <Route path="/worker/profile" element={
                  <RequireWorkerProfile>
                    <WorkerProfile />
                  </RequireWorkerProfile>
                } />
                <Route path="/worker/identity-card" element={
                  <ProtectedRoute>
                    <WorkerIdentityCard />
                  </ProtectedRoute>
                } />
                <Route path="/search" element={<SearchWorkers />} />
                <Route path="/workers/:workerId" element={<ViewWorkerProfile />} />
                <Route path="/bookings" element={
                  <RequireBasicProfile>
                    <MyBookings />
                  </RequireBasicProfile>
                } />
                <Route path="/bookings/:bookingId" element={
                  <RequireBasicProfile>
                    <BookingDetails />
                  </RequireBasicProfile>
                } />
                {/* Singular deep-link alias used in WhatsApp booking links
                    (https://www.ulcaapp.com/booking/:bookingId). Renders the
                    same Booking Details screen so the worker lands directly on
                    the booking with Accept/Decline actions. */}
                <Route path="/booking/:bookingId" element={
                  <RequireBasicProfile>
                    <BookingDetails />
                  </RequireBasicProfile>
                } />
                <Route path="/book-worker/:workerId" element={
                  <RequireBasicProfile>
                    <BookWorker />
                  </RequireBasicProfile>
                } />
                <Route path="/notifications" element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                } />
                <Route path="/job-requests" element={<JobRequestsList />} />
                <Route path="/job-requests/:jobRequestId" element={<JobRequestDetails />} />
                <Route path="/my-job-requests" element={
                  <ProtectedRoute>
                    <MyPostedJobRequests />
                  </ProtectedRoute>
                } />
                <Route path="/post-work-request" element={
                  <ProtectedRoute>
                    <PostWorkRequest />
                  </ProtectedRoute>
                } />
                <Route path="/subscription" element={<Navigate to="/profile#account" replace />} />
                
                {/* Public worker verification (QR code target) */}
                <Route path="/verify-worker/:workerId" element={<VerifyWorker />} />

                {/* Legal Pages - Public */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                
                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              </Suspense>
              </RoutedContent>
              <BottomNavBar />
              
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
              <InstallPrompt />
              <LocationBackfillPrompt />
              <NotificationOptIn />
            </div>
          </Router>
          </PWAInstallProvider>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;